import React from 'react';

import { useRouter } from 'next/router';

import {
  LabelType,
  ProductListingFilterStyle,
  useBreakpoint,
} from '@techstyle/react-components';
import {
  FormattedMessage,
  defineMessages,
  useIntl,
} from '@techstyle/react-intl';

import { defaultColors } from './colors';
import { getSectionsAndQueryFromPath } from './routing';
import { splitCombinedSize } from './sizes';

const DynamicFormattedMessage = FormattedMessage;

const filterFieldMessages = defineMessages({
  size_bra: {
    id: 'site_product_grid.bra_size_label',
    defaultMessage: 'Bra Size',
  },
  size_undie: {
    id: 'site_product_grid.undie_size_label',
    defaultMessage: 'Undie Size',
  },
  size_bralette: {
    id: 'site_product_grid.bralette_size_label',
    defaultMessage: 'Bralette Size',
  },
  size_lingerie: {
    id: 'site_product_grid.lingerie_size_label',
    defaultMessage: 'Lingerie Size',
  },
  band_size: {
    id: 'site_product_grid.band_size_label',
    defaultMessage: 'Band Size',
  },
  cup_size: {
    id: 'site_product_grid.cup_size_label',
    defaultMessage: 'Cup Size',
  },
  size: {
    id: 'site_product_grid.size_label',
    defaultMessage: 'Size',
  },
  color: { id: 'site_product_grid.color_label', defaultMessage: 'Color' },
  fabric: { id: 'site_product_grid.fabric_label', defaultMessage: 'Fabric' },
  collection: {
    id: 'site_product_grid.collection_label',
    defaultMessage: 'Collection',
  },
  sort: {
    id: 'site_product_grid.sort_by_label',
    defaultMessage: 'Sort By',
  },
  product_category_id_list: {
    id: 'site_product_grid.category_label',
    defaultMessage: 'Category',
  },
});

const sortOptionMessages = defineMessages({
  fplasc: { id: 'site_product_option.default', defaultMessage: 'Default' }, // default
  newest: {
    id: 'site_product_option.sort_by_new_arrivals',
    defaultMessage: 'New Arrivals',
  },
  newarrivals: {
    id: 'site_product_option.sort_by_new_arrivals',
    defaultMessage: 'New Arrivals',
  },
  bestsellers: {
    id: 'site_product_option.sort_by_best_sellers',
    defaultMessage: 'Best Sellers',
  },
  pricel2h: {
    id: 'site_product_option.sort_by_price_low_high',
    defaultMessage: 'Price: Low to High',
  },
  priceh2l: {
    id: 'site_product_option.sort_by_price_high_to_low',
    defaultMessage: 'Price: High to Low',
  },
  relevance: {
    id: 'site_product_option.sort_by_relevance',
    defaultMessage: 'Relevance',
  },
});

const addZeroWidthSpaces = label => {
  return label.replace(/\//g, '/\u200B');
};

export function useCategoryLabels() {
  const intl = useIntl();
  const router = useRouter();
  const { section } = getSectionsAndQueryFromPath(router.asPath);

  const renderLabel = (category, labelOptions) => {
    const { labelType, isTopLevel } = labelOptions;

    if (category.label === 'New') {
      return <FormattedMessage id="site_navigation.new" defaultMessage="New" />;
    }

    switch (labelType) {
      case LabelType.CATEGORY_TREE:
        if (isTopLevel) {
          return (
            <FormattedMessage
              id="site_product_option.category_view_all"
              defaultMessage="View All"
            />
          );
        }
        break;
      case LabelType.CATEGORY_DROPDOWN:
      case LabelType.TOP_LEVEL_CATEGORY_HEADING:
        // using section here to format the topLevel label, since category label
        // from Navigationizer can be either string or rb
        if (isTopLevel && section) {
          return (
            <DynamicFormattedMessage
              id={`site_product_category.all_${section}`}
              defaultMessage={`All ${section}`}
            />
          );
        }
        break;
    }

    if (category.isResourceBundle) {
      return (
        <DynamicFormattedMessage
          id={category.label}
          defaultMessage={category.label}
        />
      );
    }

    return category.label;
  };

  const renderLabelString = (category, labelOptions = {}) => {
    const { labelType, isTopLevel } = labelOptions;

    if (category.label === 'New') {
      return intl.formatMessage({
        id: 'site_navigation.new',
        defaultMessage: 'New',
      });
    }

    switch (labelType) {
      case LabelType.CATEGORY_TREE:
        if (isTopLevel) {
          return intl.formatMessage({
            id: 'site_product_option.category_view_all',
            defaultMessage: 'View All',
          });
        }
        break;
      case LabelType.CATEGORY_DROPDOWN:
        // using section here to format the topLevel label, since category label
        // from Navigationizer can be either string or rb
        if (isTopLevel && section) {
          return intl.formatMessage({
            id: `site_product_category.all_${section}`,
            defaultMessage: `All ${section}`,
          });
        }
        break;
    }

    if (category.isResourceBundle) {
      return intl.formatMessage({
        id: category.label,
        defaultMessage: category.label,
      });
    }

    return category.label;
  };

  return { renderLabel, renderLabelString };
}

export function useFilterFieldLabels(
  showNewFilteringAndSortingUxInDesktopFlag = false,
  showNewFilteringAndSortingUxInMobileFlag = false
) {
  const intl = useIntl();
  const { isDesktop } = useBreakpoint();

  const renderLabel = (filterField, { labelType } = {}) => {
    switch (labelType) {
      case LabelType.PRODUCT_FILTER:
      case LabelType.PRODUCT_FILTER_TAG: {
        const message = filterFieldMessages[filterField.field];
        if (message) {
          return <DynamicFormattedMessage {...message} />;
        }
        break;
      }
      case LabelType.SORT_DROPDOWN: {
        if (
          (showNewFilteringAndSortingUxInDesktopFlag && isDesktop) ||
          (showNewFilteringAndSortingUxInMobileFlag && !isDesktop)
        ) {
          const message = filterFieldMessages[filterField.field];
          if (message) {
            return <DynamicFormattedMessage {...message} />;
          }
        }
        break;
      }
    }
    return filterField.label;
  };

  const renderLabelString = (filterField, { labelType } = {}) => {
    switch (labelType) {
      case LabelType.PRODUCT_FILTER:
      case LabelType.PRODUCT_FILTER_TAG: {
        const message = filterFieldMessages[filterField.field];
        if (message) {
          return intl.formatMessage(message);
        }
        break;
      }
    }
    return filterField.label;
  };

  return { renderLabel, renderLabelString };
}

export function useOptionLabels({ isBuilder = false } = {}) {
  const intl = useIntl();

  const renderLabel = (filterField, option, labelOptions = {}) => {
    const { filterStyle, whiteSpace } = labelOptions;
    let label =
      option.label || option.defaultMessage || option.value?.toString();

    switch (filterField.field) {
      case 'sort': {
        if (isBuilder) {
          return label;
        }
        const message = sortOptionMessages[option.value];
        if (message) {
          return (
            <DynamicFormattedMessage {...message} whiteSpace={whiteSpace} />
          );
        }
        break;
      }
      case 'color':
      case 'fabric': {
        const message = defaultColors[option.value];
        if (message) {
          return (
            <DynamicFormattedMessage {...message} whiteSpace={whiteSpace} />
          );
        }
        break;
      }
      case 'band_size':
      case 'cup_size':
        return label;
    }

    label =
      filterStyle === ProductListingFilterStyle.GRID // size options
        ? addZeroWidthSpaces(label)
        : label;

    const { splitSize, sizeSplitter } = splitCombinedSize(label);
    const isSizeDisplayableInNewLine =
      splitSize.length > 1 && splitSize.some(size => size.length > 2);

    return isSizeDisplayableInNewLine && sizeSplitter === ' ' ? (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span
          style={{
            fontSize: 12,
            whiteSpace: 'pre-line',
          }}
        >
          {splitSize[0]}
        </span>
        <span
          style={{
            fontSize: 10,
            whiteSpace: 'pre-line',
          }}
        >
          {splitSize[1]}
        </span>
      </div>
    ) : label?.length > 4 ? (
      <span style={{ fontSize: 11 }}>{label}</span>
    ) : (
      label
    );
  };

  const renderLabelString = (filterField, option, labelOptions = {}) => {
    const { filterStyle } = labelOptions;
    const label =
      option.label || option.defaultMessage || option.value.toString();
    switch (filterField.field) {
      case 'sort': {
        const message = sortOptionMessages[option.value];
        if (message) {
          return intl.formatMessage(message);
        }
        break;
      }
      case 'color':
      case 'fabric': {
        const message = defaultColors[option.value];
        if (message) {
          return intl.formatMessage(message);
        }
        break;
      }
      case 'band_size':
      case 'cup_size':
        return option.value;
    }

    return filterStyle === ProductListingFilterStyle.GRID // size options
      ? addZeroWidthSpaces(label)
      : label;
  };

  return { renderLabel, renderLabelString };
}

export function useTransformSortLabel() {
  return useOptionLabels({ isBuilder: true });
}
